/**
 * Select Practitioner Step (V2)
 *
 * Only shown when an appointment type has multiple eligible practitioners
 * at the clinic (e.g. clinic-wide types). Skipped automatically by the hook
 * when there's only one option.
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronRight, UserRound } from 'lucide-react';
import type { ClinicTypeBookingPage } from '@/services/bookingService';

interface SelectPractitionerStepProps {
  practitioners: ClinicTypeBookingPage['practitioners'];
  onSelect: (practitionerId: string) => void;
}

export const SelectPractitionerStep: React.FC<SelectPractitionerStepProps> = ({
  practitioners,
  onSelect,
}) => {
  return (
    <div>
      <h2 className='mb-2 text-2xl font-bold text-slate-900'>Choose your provider</h2>
      <p className='mb-6 text-slate-500'>
        Multiple providers offer this appointment type. Select one to continue.
      </p>

      <div className='space-y-3'>
        {practitioners.map(p => (
          <Card
            key={p.id}
            className='cursor-pointer border-slate-200 transition-colors hover:border-teal-500'
            onClick={() => onSelect(p.id)}
          >
            <CardContent className='flex items-center gap-4 py-4'>
              <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-teal-50'>
                <UserRound className='h-5 w-5 text-teal-600' />
              </div>
              <div className='min-w-0 flex-1'>
                <p className='truncate text-base font-semibold text-slate-900'>
                  {p.full_name}
                  {p.credentials ? (
                    <span className='ml-1 text-sm font-normal text-slate-500'>
                      , {p.credentials}
                    </span>
                  ) : null}
                </p>
                {p.specialization && (
                  <p className='truncate text-sm text-slate-500'>{p.specialization}</p>
                )}
              </div>
              <ChevronRight className='h-5 w-5 flex-shrink-0 text-slate-400' />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
